import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Form, FormLayout, Select, TextField } from '@shopify/polaris';
import type { SubmitHandler } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import AppModal from '@/components/AppModal/AppModal';
import { deliverySettingSelector, handleSaveDeliverySettings } from '@/redux/slice/delivery.slice';
import { useDispatch, useSelector } from 'react-redux';
import { TRegion } from './DeliveryLocation';

type TModalEditZone = {
  isShowModal: boolean;
  locationCities: TRegion[];
  onHide: () => void;
};

type TFormeEditZone = {
  name: string;
  city: string;
  additionalNote: string;
};

const ModalEditZone = ({ isShowModal, locationCities, onHide }: TModalEditZone) => {
  const { t } = useTranslation('deliverySettings');
  const deliverySettings = useSelector(deliverySettingSelector);
  const dispatch = useDispatch();

  const schema = z.object({
    name: z.string().trim().min(1, { message: 'Zone name is require' }),
    city: z.string().trim().min(1, { message: 'City name is require' }),
    additionalNote: z.string().optional(),
  });

  const {
    control,
    handleSubmit,
    reset,
    clearErrors,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues: {
      name: deliverySettings?.name ?? '',
      city: deliverySettings?.city ?? '',
      additionalNote: deliverySettings?.additionalNote ?? '',
    },
    values: {
      name: deliverySettings?.name ?? '',
      city: deliverySettings?.city ?? '',
      additionalNote: deliverySettings?.additionalNote ?? '',
    },
    resolver: zodResolver(schema),
  });

  const handleSubmitData: SubmitHandler<TFormeEditZone> = (data) => {
    dispatch(
      handleSaveDeliverySettings({
        name: data.name.trim(),
        city: data.city,
        additionalNote: data.additionalNote.trim(),
      }),
    );
    onHide();
  };

  const handleCancel = () => {
    onHide();
    reset();
    clearErrors();
  };

  return (
    <AppModal
      open={isShowModal}
      onCancel={handleCancel}
      onSubmit={handleSubmit(handleSubmitData)}
      title={t('sections.locationAddress.editZone')}
      cancelText={t('sections.locationAddress.cancel')}
      submitText={t('sections.locationAddress.save')}
    >
      <Box padding={'400'}>
        <Form onSubmit={handleSubmit(handleSubmitData)}>
          <FormLayout>
            <Controller
              name="name"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <TextField
                  label={t('sections.locationAddress.zoneName')}
                  {...field}
                  onBlur={() => {
                    setValue('name', field.value.trim());
                  }}
                  requiredIndicator
                  error={errors.name?.message}
                  autoComplete="off"
                />
              )}
            />
            <Controller
              name="city"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <Select
                  label={t('sections.locationAddress.city')}
                  placeholder={t('sections.locationAddress.placeholder')}
                  {...field}
                  options={locationCities}
                  requiredIndicator
                  error={errors.city?.message}
                />
              )}
            />
            <Controller
              name="additionalNote"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <TextField
                  label={t('sections.locationAddress.additionalNote')}
                  {...field}
                  onBlur={() => {
                    setValue('additionalNote', field.value.trim());
                  }}
                  autoComplete="off"
                />
              )}
            />
          </FormLayout>
        </Form>
      </Box>
    </AppModal>
  );
};

export default ModalEditZone;
